import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { foodsApi } from '@/apis/nutrition/foodsApi';
import { tagsApi } from '@/apis/nutrition/tagsApi';
import { recommendationsApi } from '@/services/recommendations-api';
import { fetchAccessToken } from '@/utils/auth';
import { PaletteNode, BuilderMode } from '../types/builder';
import { recordReferenceLoadIssues, useReferenceLoadIssues } from '../utils/referenceLoadIssues';

export const BUILDER_STALE_TIME = 5 * 60 * 1000;
export const fetchSuperclinicCodes = async (): Promise<PaletteNode[]> => {
  const token = await fetchAccessToken();
  const response = await axios.get('references/catalog/registry', {
    params: { is_active: true },
    headers: { Authorization: `Bearer ${token}` },
  });
  
  const data = Array.isArray(response.data) ? response.data : response.data.items || [];
  return data.map((item: any) => ({
    id: item.code,
    type: 'superclinic_code' as const,
    category: 'conditions' as const,
    name: item.name || item.description || item.code,
    description: item.description,
    code: item.code,
    metadata: item,
  }));
};
const fetchTemplates = async (): Promise<PaletteNode[]> => {
  const token = await fetchAccessToken();
  const response = await axios.get('clinical/templates', {
    params: { page_size: 100 },
    headers: { Authorization: `Bearer ${token}` },
  });
  
  let templates = [];
  if (Array.isArray(response.data)) {
    templates = response.data;
  } else if (response.data?.results) {
    templates = response.data.results;
  } else if (response.data?.items) {
    templates = response.data.items;
  }
  
  return templates.map((item: any) => ({
    id: item.template_id || item.id,
    type: 'template' as const,
    category: 'actions' as const,
    name: item.name,
    description: item.description,
    metadata: item,
  }));
};
const TYPE_TO_ID_FIELD: Record<string, string> = {
  pharmaceuticals: 'pharmaceutical_id',
  nutraceuticals: 'nutraceutical_id',
  labs: 'lab_id',
  symptoms: 'symptom_id',
  signs: 'sign_id',
  diagnoses: 'diagnosis_id',
  diets: 'diet_id',
  therapies: 'therapy_id',
  advices: 'advice_id',
  food_catalog: 'food_id',
  food_tags: 'food_tag_id',
};

const resolveReferenceItemId = (
  item: Record<string, unknown>,
  idField: string
): string | null => {
  const candidates = [item.id, item[idField], item.uuid];

  for (const candidate of candidates) {
    if (candidate !== undefined && candidate !== null && String(candidate).trim() !== '') {
      return String(candidate);
    }
  }

  return null;
};
const fetchReferenceData = async (
  endpoint: string,
  type: PaletteNode['type'],
  category: PaletteNode['category'],
  pageSize = 500
): Promise<PaletteNode[]> => {
  const token = await fetchAccessToken();
  const response = await axios.get(`references/${endpoint}`, {
    params: { page_size: pageSize },
    headers: { Authorization: `Bearer ${token}` },
  });

  let items = [];
  if (Array.isArray(response.data)) {
    items = response.data;
  } else if (response.data?.results) {
    items = response.data.results;
  } else if (response.data?.items) {
    items = response.data.items;
  }

  const idField = TYPE_TO_ID_FIELD[type] || `${type}_id`;
  const seenIds = new Set<string>();
  let skippedMissingIdCount = 0;
  let skippedDuplicateCount = 0;

  const nodes = items.flatMap((item: Record<string, unknown>) => {
    const id = resolveReferenceItemId(item, idField);
    if (!id) {
      skippedMissingIdCount += 1;
      return [];
    }

    if (seenIds.has(id)) {
      skippedDuplicateCount += 1;
      return [];
    }
    seenIds.add(id);

    return [{
      id,
      type,
      category,
      name: item.name as string,
      description: (item.description || item.notes) as string | undefined,
      metadata: item,
    }];
  });

  recordReferenceLoadIssues({
    endpoint,
    skippedMissingIdCount,
    skippedDuplicateCount,
  });

  return nodes;
};

export const fetchSymptoms = () =>
  fetchReferenceData('symptoms', 'symptoms', 'symptoms');

export const fetchSigns = () => fetchReferenceData('signs', 'signs', 'signs');

export const fetchDiagnoses = () =>
  fetchReferenceData('diagnoses', 'diagnoses', 'diagnoses');
export const useSuperclinicCodes = (enabled = true) => {
  return useQuery({
    queryKey: ['builder', 'superclinic-codes'],
    queryFn: fetchSuperclinicCodes,
    enabled,
    staleTime: BUILDER_STALE_TIME,
  });
};

export const useBuilderTemplates = (enabled = true) => {
  return useQuery({
    queryKey: ['builder', 'templates'],
    queryFn: fetchTemplates,
    enabled,
    staleTime: BUILDER_STALE_TIME,
  });
};

export const usePharmaceuticals = (enabled = true) => {
  return useQuery({
    queryKey: ['builder', 'pharmaceuticals'],
    queryFn: () => fetchReferenceData('pharmaceuticals', 'pharmaceuticals', 'pharmaceuticals'),
    enabled,
    staleTime: BUILDER_STALE_TIME,
  });
};

export const useNutraceuticals = (enabled = true) => {
  return useQuery({
    queryKey: ['builder', 'nutraceuticals'],
    queryFn: () => fetchReferenceData('nutraceuticals', 'nutraceuticals', 'nutraceuticals'),
    enabled,
    staleTime: BUILDER_STALE_TIME,
  });
};

export const useLabs = (enabled = true) => {
  return useQuery({
    queryKey: ['builder', 'labs'],
    queryFn: () => fetchReferenceData('labs', 'labs', 'labs'),
    enabled,
    staleTime: BUILDER_STALE_TIME,
  });
};

export const useSymptoms = (enabled = true) => {
  return useQuery({
    queryKey: ['builder', 'symptoms'],
    queryFn: fetchSymptoms,
    enabled,
    staleTime: BUILDER_STALE_TIME,
  });
};

export const useSigns = (enabled = true) => {
  return useQuery({
    queryKey: ['builder', 'signs'],
    queryFn: fetchSigns,
    enabled,
    staleTime: BUILDER_STALE_TIME,
  });
};

export const useDiagnoses = (enabled = true) => {
  return useQuery({
    queryKey: ['builder', 'diagnoses'],
    queryFn: fetchDiagnoses,
    enabled,
    staleTime: BUILDER_STALE_TIME,
  });
};

export const useDiets = (enabled = true) => {
  return useQuery({
    queryKey: ['builder', 'diets'],
    queryFn: () => fetchReferenceData('diets', 'diets', 'diets'),
    enabled,
    staleTime: BUILDER_STALE_TIME,
  });
};

export const useTherapies = (enabled = true) => {
  return useQuery({
    queryKey: ['builder', 'therapies'],
    queryFn: () => fetchReferenceData('therapies', 'therapies', 'therapies'),
    enabled,
    staleTime: BUILDER_STALE_TIME,
  });
};

export const useAdvices = (enabled = true) => {
  return useQuery({
    queryKey: ['builder', 'advices'],
    queryFn: () => fetchReferenceData('advices', 'advices', 'advices'),
    enabled,
    staleTime: BUILDER_STALE_TIME,
  });
};

export const useLifestyles = (enabled = true) => {
  return useQuery({
    queryKey: ['builder', 'lifestyles'],
    queryFn: async (): Promise<PaletteNode[]> => {
      const token = await fetchAccessToken();
      const response = await axios.get('references/lifestyles', {
        params: { page_size: 500 },
        headers: { Authorization: `Bearer ${token}` },
      });

      let items = [];
      if (Array.isArray(response.data)) {
        items = response.data;
      } else if (response.data?.results) {
        items = response.data.results;
      } else if (response.data?.items) {
        items = response.data.items;
      }

      return items.map((item: any) => ({
        id: item.lifestyle_id || item.id || item.uuid,
        type: 'advices' as const,
        category: 'advices' as const,
        name: item.name,
        description: item.description || item.notes,
        code: item.superclinic_code || undefined,
        metadata: item,
      }));
    },
    enabled,
    staleTime: BUILDER_STALE_TIME,
  });
};

export const useFoodCatalog = (enabled = true) => {
  return useQuery({
    queryKey: ['builder', 'food-catalog'],
    queryFn: async (): Promise<PaletteNode[]> => {
      const response = await foodsApi.list({}, 1, 500);
      const items = Array.isArray(response) ? response : response.results || [];
      return items.map((item) => ({
        id: String(item.id),
        type: 'food_catalog' as const,
        category: 'food_catalog' as const,
        name: item.name,
        description: item.description || `${item.calories} cal, ${item.protein}g protein`,
        metadata: item,
      }));
    },
    enabled,
    staleTime: BUILDER_STALE_TIME,
  });
};

export const useFoodTags = (enabled = true) => {
  return useQuery({
    queryKey: ['builder', 'food-tags'],
    queryFn: async (): Promise<PaletteNode[]> => {
      const tags = await tagsApi.list({ is_active: true });
      return tags.map((tag) => ({
        id: String(tag.id),
        type: 'food_tags' as const,
        category: 'food_tags' as const,
        name: tag.name,
        description: tag.description || `${tag.category} tag`,
        metadata: tag as unknown as Record<string, unknown>,
      }));
    },
    enabled,
    staleTime: BUILDER_STALE_TIME,
  });
};
export const useConditionsForLinker = (enabled = true) => {
  return useQuery({
    queryKey: ['builder', 'conditions-linker'],
    queryFn: async (): Promise<PaletteNode[]> => {
      const conditions = await recommendationsApi.getAllConditions({ page_size: 500 });
      return conditions.map((item: any) => ({
        id: item.condition_id,
        type: 'condition' as const,
        category: 'conditions' as const,
        name: item.name,
        description: item.description,
        metadata: item,
      }));
    },
    enabled,
    staleTime: BUILDER_STALE_TIME,
  });
};
export const useProtocolsForLinker = (enabled = true) => {
  return useQuery({
    queryKey: ['builder', 'protocols-linker'],
    queryFn: async (): Promise<PaletteNode[]> => {
      const protocols = await recommendationsApi.getAllProtocols({ page_size: 500 });
      return protocols.map((item: any) => ({
        id: item.protocol_id || item.id,
        type: 'protocol' as const,
        category: 'protocols' as const,
        name: item.name,
        description: item.description,
        metadata: item,
      }));
    },
    enabled,
    staleTime: BUILDER_STALE_TIME,
  });
};
export const useAvailableNodes = (mode: BuilderMode) => {
  const referenceLoadIssues = useReferenceLoadIssues();
  const isConditionMode = mode === 'condition';
  const isProtocolMode = mode === 'protocol';
  const isTemplateMode = mode === 'template';
  const needsConditionCodeMaps = isConditionMode || isProtocolMode;
  const superclinicCodes = useSuperclinicCodes(isConditionMode);
  const pharmaceuticals = usePharmaceuticals(isProtocolMode);
  const nutraceuticals = useNutraceuticals(isProtocolMode);
  const labs = useLabs(isProtocolMode);
  const symptoms = useSymptoms(needsConditionCodeMaps);
  const signs = useSigns(needsConditionCodeMaps);
  const diagnoses = useDiagnoses(needsConditionCodeMaps);
  const diets = useDiets(isProtocolMode);
  const therapies = useTherapies(isProtocolMode);
  const advices = useAdvices(false);
  const lifestyles = useLifestyles(isProtocolMode);
  const foodCatalog = useFoodCatalog(isProtocolMode);
  const foodTags = useFoodTags(isProtocolMode);
  const conditionsForLinker = useConditionsForLinker(isTemplateMode);
  const protocolsForLinker = useProtocolsForLinker(isTemplateMode);

  const isLoading = isConditionMode
    ? superclinicCodes.isLoading ||
      symptoms.isLoading ||
      signs.isLoading ||
      diagnoses.isLoading
    : isProtocolMode
    ? pharmaceuticals.isLoading ||
      nutraceuticals.isLoading ||
      labs.isLoading ||
      symptoms.isLoading ||
      signs.isLoading ||
      diagnoses.isLoading ||
      diets.isLoading ||
      therapies.isLoading ||
      lifestyles.isLoading ||
      foodCatalog.isLoading ||
      foodTags.isLoading
    : conditionsForLinker.isLoading || protocolsForLinker.isLoading;

  return {
    isLoading,
    referenceLoadIssues,
    superclinicCodes: superclinicCodes.data || [],
    pharmaceuticals: pharmaceuticals.data || [],
    nutraceuticals: nutraceuticals.data || [],
    labs: labs.data || [],
    symptoms: symptoms.data || [],
    signs: signs.data || [],
    diagnoses: diagnoses.data || [],
    diets: diets.data || [],
    therapies: therapies.data || [],
    advices: advices.data || [],
    lifestyles: lifestyles.data || [],
    food_catalog: foodCatalog.data || [],
    food_tags: foodTags.data || [],
    conditions: conditionsForLinker.data || [],
    protocols: protocolsForLinker.data || [],
  };
};
